/**
 * FormulaCell Component
 * Story 6.4: Implement Column Formulas
 * AC-6.4.2: Formula cells display calculated values with locked styling
 * AC-6.4.5: Visual distinction for formula cells (different background, italic font)
 * AC-6.4.3: Error display with FormulaErrorTooltip
 *
 * Renders computed formula values with visual distinction:
 * - Background: --surface-2 token (bg-slate-100 light / bg-slate-700 dark)
 * - Font style: italic
 * - Cursor: default (read-only)
 */

import React from 'react';
import { FormulaErrorTooltip, isFormulaErrorCode, getFormulaErrorCellStyles } from './FormulaErrorTooltip';
import type { FormulaError } from '../../../services/FormulaEngine';

export interface FormulaCellProps {
  /** Computed value to display */
  value: unknown;
  /** Optional error if formula evaluation failed */
  error?: FormulaError | null;
  /** Column semantic type for formatting (money, percentage, etc.) */
  semanticType?: string;
  /** Number format configuration */
  numberFormat?: {
    decimals?: number;
  };
  /** Additional CSS classes */
  className?: string;
}

/**
 * Format a numeric value based on semantic type and number format
 */
function formatValue(
  value: unknown,
  semanticType?: string,
  numberFormat?: { decimals?: number }
): string {
  if (value === null || value === undefined) {
    return '';
  }

  // Check if value is an error code string
  if (typeof value === 'string' && isFormulaErrorCode(value)) {
    return value;
  }

  // Format numbers based on semantic type
  if (typeof value === 'number') {
    const decimals = numberFormat?.decimals;

    switch (semanticType) {
      case 'money':
      case 'currency': {
        const opts: Intl.NumberFormatOptions = {
          style: 'currency',
          currency: 'USD',
          ...(decimals !== undefined && {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          }),
        };
        return value.toLocaleString('en-US', opts);
      }

      case 'percentage':
      case 'percent': {
        const pctValue = value * 100;
        if (decimals !== undefined) {
          return `${pctValue.toFixed(decimals)}%`;
        }
        return `${pctValue.toFixed(1)}%`;
      }

      case 'duration': {
        // Duration in minutes
        if (value < 0) return '—';
        const hours = Math.floor(value / 60);
        const mins = Math.round(value % 60);
        if (hours === 0) return `${mins}m`;
        if (mins === 0) return `${hours}h`;
        return `${hours}h ${mins}m`;
      }

      default: {
        // General number formatting
        if (decimals !== undefined) {
          return value.toLocaleString('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          });
        }
        return value.toLocaleString('en-US');
      }
    }
  }

  // Boolean values
  if (typeof value === 'boolean') {
    return value ? 'TRUE' : 'FALSE';
  }

  // Date values
  if (value instanceof Date) {
    return value.toLocaleDateString('en-US');
  }

  // Default: convert to string
  return String(value);
}

/**
 * FormulaCell - Renders computed formula values with visual distinction
 * AC-6.4.5: Formula cells have different background color and italic font
 */
export function FormulaCell({
  value,
  error,
  semanticType,
  numberFormat,
  className = '',
}: FormulaCellProps): React.ReactElement {
  // Check if value itself is an error code (from HyperFormula)
  const isErrorValue = typeof value === 'string' && isFormulaErrorCode(value);

  // If there's an error object or error value, render error state
  if (error || isErrorValue) {
    const errorObj: FormulaError = error || {
      code: (value as string) as FormulaError['code'],
      message: 'Formula evaluation error',
    };

    return (
      <div
        className={`
          formula-cell formula-cell-error
          ${getFormulaErrorCellStyles()}
          cursor-default select-none
          w-full h-full flex items-center
          ${className}
        `}
        data-testid="formula-cell-error"
      >
        <FormulaErrorTooltip error={errorObj} />
      </div>
    );
  }

  // Format the computed value
  const displayValue = formatValue(value, semanticType, numberFormat);

  return (
    <div
      className={`
        formula-cell
        cursor-default select-none
        w-full h-full flex items-center
        text-foreground
        ${className}
      `}
      data-testid="formula-cell"
      title="Formula (read-only)"
    >
      <span className="truncate w-full">{displayValue}</span>
    </div>
  );
}

/**
 * Get CSS class names for formula cell styling in grid context
 * Used by SmartGrid when rendering formula-type columns
 */
export function getFormulaCellClasses(): string {
  return 'cursor-default';
}

/**
 * Check if a column is a formula column that should use FormulaCell rendering
 */
export function isFormulaColumn(type: string, formulaExpression?: string): boolean {
  return type === 'formula' && !!formulaExpression;
}

export default FormulaCell;
